import { useState } from 'react';
import { trpc } from '@web/lib/trpc';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@web/components/ui/dialog';
import { Button } from '@web/components/ui/button';
import { Input } from '@web/components/ui/input';
import { Label } from '@web/components/ui/label';
import { Spinner } from '@web/components/ui/spinner';
import { toast } from 'sonner';
import { Checkbox } from '@web/components/ui/checkbox';

type CreateUserDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function CreateUserDialog({
  open,
  onOpenChange,
}: CreateUserDialogProps) {
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

  const utils = trpc.useUtils();

  // Fetch all available roles
  const { data: allRoles, isLoading: isLoadingRoles } =
    trpc.user.listRoles.useQuery();

  // Create user mutation
  const createUserMutation = trpc.user.createUser.useMutation({
    onSuccess: () => {
      // Reset form
      setFirstName('');
      setLastName('');
      setEmail('');
      setPassword('');
      setSelectedRoleIds([]);

      // Invalidate queries to refresh data
      void utils.user.listUsers.invalidate();

      toast.success('New user has been created successfully.');

      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create user. Please try again.');
    },
  });

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedRoleIds.length === 0) {
      toast.error('Please select at least one role for the user.');
      return;
    }

    createUserMutation.mutate({
      firstName,
      lastName,
      email,
      password,
      roleIds: selectedRoleIds,
    });
  };

  // Handle role selection toggle
  const handleRoleToggle = (roleId: string) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId],
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="block max-h-[90vh] w-[90vw] max-w-none overflow-y-auto rounded-lg border-gray-200 p-6 shadow-lg md:w-[80vw] lg:w-[60vw] lg:max-w-[600px]">
        <DialogHeader className="mb-4">
          <DialogTitle className="text-left">Create New User</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                  }}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                  }}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
                required
                minLength={8}
              />
              <p className="text-xs text-gray-500">
                Password must be at least 8 characters long.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Roles</Label>
              {isLoadingRoles ? (
                <div className="flex h-10 items-center">
                  <Spinner className="h-4 w-4" />
                  <span className="ml-2 text-sm">Loading roles...</span>
                </div>
              ) : (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {allRoles?.map((role) => (
                    <div
                      key={role.roleId}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={`new-role-${role.roleId}`}
                        checked={selectedRoleIds.includes(role.roleId)}
                        onCheckedChange={() => {
                          handleRoleToggle(role.roleId);
                        }}
                      />
                      <Label
                        htmlFor={`new-role-${role.roleId}`}
                        className="text-sm font-normal"
                      >
                        {role.name}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                createUserMutation.isPending || selectedRoleIds.length === 0
              }
            >
              {createUserMutation.isPending ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
